import type { CardDef } from '../../../../core/card';
import { cardDescription, cardName } from '../../../../core/cardText';
import { LIGHT } from '../../../palette';
import { plateCode } from '../codes';
import {
  hashStr,
  hand,
  pt,
  rect as rectPts,
  ring,
  rotateAbout,
  rng,
  type Pt,
} from '../geom';
import { crossScribble, halftoneDots } from '../hatch';
import { t } from '../../../../i18n';
import { handRing, inkPrims, sparkle, stink, xEyes } from '../ink';
import {
  box,
  dot,
  fillPoly,
  group,
  label,
  line,
  solid,
  type FaceSpec,
  type FontRole,
  type Prim,
} from '../spec';

/**
 * **夹页 Insert —— 五张无效牌。**
 *
 * 它们是**被人塞进来的**东西：走私品、素人涂鸦、剪报。
 * Tate 对 outsider art 的定义里就写着它包含「囚犯创作的艺术」。
 *
 * > **喜剧结构：画得越用力、越郑重，机器盖的那个 `∅` 就越好笑。**
 *
 * 这五件东西在原作里是同一个笑话 —— 投入巨大 / 看起来很凶，实际毫无用处。
 * 所以画面要**认真过头**：涂满、臭气线、星芒、叉叉眼、双道轮廓。
 *
 * ## 一条硬规矩：它们**不出框**
 *
 * 破框是"活牌"的信号，夹页不是活牌 —— 它们是夹进来的，本来就该守自己的边。
 * 所以这一族**没有 `spill`**，一切都写在 `prims` 里（会被裁到牌内）。
 * 整张内容还刻意**歪 1.6°、放大 6%** 贴上去：机器装裱得整整齐齐，
 * 而它是手贴上来的。
 *
 * ## 零酸色
 *
 * 这五张在桌面上是**视觉静音区**。所有墨都从 `LIGHT` 取，一个酸色都不许进来。
 */

/** 档案号。⚠️ 版号 `NUL` 五张全一样（ASCII 的空字符 0x00），序号才是唯一的那一层 */
const ARCHIVE: Readonly<Record<string, string>> = {
  Powerless1: '01',
  Powerless2: '02',
  Powerless3: '03',
  Powerless4: '04',
  Powerless5: '05',
};

/**
 * **外来品名** —— 它们不是这台机器起的名字。
 *
 * ⚠️ 牌面上**不出现来源游戏的名字**（版权与商标），只写物品自己的英文名。
 * 「它来自别处」这件事由**风格**（单色、勒索信、撕裂边）说，不由文字说。
 */
const FOREIGN: Readonly<Record<string, string>> = {
  Powerless1: 'DUBIOUS FOOD',
  Powerless2: "PRISONER'S COLLAR",
  Powerless3: 'NETHERITE HOE',
  Powerless4: 'LICORICE SEED',
  Powerless5: 'SNAKEBITE',
};

const INK = LIGHT.base;
const DIM = LIGHT.dim;

export function insertFace(card: CardDef): FaceSpec {
  const seed = hashStr(card.id);
  const art = insertArt(card, seed);

  // 机器装裱得整整齐齐，而这张是手贴上来的 —— 所以整块内容歪 1.6°、放大 6%
  // ⚠️ `at` 和 `pivot` 都要给 —— 子图元是绝对坐标，只想绕 (50,70) 转。
  //    只给 `at` 会把整块内容平移到 (50,70) 去（踩过，五张里三张整张白掉）
  const paste = { at: pt(50, 70), pivot: pt(50, 70), rotate: -1.6, scale: 1.06 } as const;

  return {
    ground: solid(0x000000),
    accent: DIM,
    prims: [
      group([...tornEdges(), ...art, ...voidStampAt(card), ...insertFrame(card, seed)], paste),
    ],
    press: true,
    // ⚠️ 刻意**没有 spill** —— 夹页不出框，见文件头
  };
}

// ============================================================
// 共用的框
// ============================================================

/** 空集符号。⚠️ 位置**逐张不同** —— 它标的是"这个过程在哪一点死掉"，不是贴上去的 logo */
function voidStampAt(card: CardDef): Prim[] {
  const at: Readonly<Record<string, Pt>> = {
    Powerless1: pt(80, 32),
    Powerless2: pt(80, 22),
    Powerless3: pt(52.4, 25.6),
    Powerless4: pt(74, 21),
    Powerless5: pt(72, 26),
  };
  const p = at[card.local] ?? pt(80, 26);
  const s = 0.85;
  return [
    { k: 'circle', x: p.x, y: p.y, r: 6.2, stroke: { width: 0.95 / s, color: INK, alpha: 0.85 } },
    line([pt(p.x - 4.4, p.y + 4.4), pt(p.x + 4.4, p.y - 4.4)], {
      width: 0.95 / s,
      color: INK,
      alpha: 0.85,
    }),
  ];
}

/**
 * 撕裂的上/下边。
 *
 * 黑色锯齿盖在牌的上下沿上 —— 因为牌底本身就是黑的，盖上去就"咬"出了缺口。
 * 比画一条真的曲线便宜，而且**看起来就是撕的**。
 */
function tornEdges(): Prim[] {
  // 上边：贴着 y=0 的一条带，下沿是锯齿
  const top: Pt[] = [pt(0, 0), pt(100, 0), pt(100, 3.4)];
  for (let i = 0; i < 11; i++) {
    top.push(pt(100 - (i + 1) * 9.1, i % 2 === 0 ? 1.2 : 4.6));
  }
  // 下边：贴着 y=140 的一条带，上沿是锯齿
  const bottom: Pt[] = [pt(0, 140), pt(100, 140), pt(100, 136.6)];
  for (let i = 0; i < 11; i++) {
    bottom.push(pt(100 - (i + 1) * 9.1, i % 2 === 0 ? 137.7 : 134.2));
  }
  return [fillPoly(top, solid(0x000000)), fillPoly(bottom, solid(0x000000))];
}

/** 页眉 + 档案行 + 正文 + 底部的"贴纸" */
function insertFrame(card: CardDef, seed: number): Prim[] {
  const no = ARCHIVE[card.local] ?? '00';
  return [
    // 页眉：机器盖的版号，和手贴的内容不是一套字
    label(plateCode(card.local), 9, 11.5, { size: 4.2, color: INK, tracking: 0.9 }),
    line([pt(7, 13.5), pt(93, 13.5)], { width: 0.45, color: INK, alpha: 0.55 }),
    label(t('render.category.powerless'), 93, 11.5, {
      size: 4.1,
      color: DIM,
      role: 'cjk',
      anchor: 'end',
      tracking: 0.9,
    }),

    // 档案行：编号 + 外来品名 + 机器的判决
    label(`NUL-${no}  ·  ${FOREIGN[card.local] ?? ''}`, 7, 88, {
      size: 2.8,
      color: DIM,
      tracking: 0.5,
    }),
    label('EFFECT ∅', 93, 88, { size: 2.8, color: INK, anchor: 'end', tracking: 0.5 }),

    // 正文块
    box(0, 94, 100, 46, solid(0x000000, 0.72)),
    ...ransom(card, { x: 7, y: 108, size: 11.5 }, seed ^ 0x5a),
    label(cardDescription(card), 7, 123, {
      size: 3.4,
      color: DIM,
      role: 'cjk',
      wrap: 24,
      lineHeight: 4.6,
    }),
    // 底下那条白边 —— 像一截没撕干净的胶带
    box(0, 135, 100, 4.5, solid(INK, 0.5)),
  ];
}

/**
 * **勒索信牌名。**
 *
 * 每个字从"别处剪下来"：白纸黑字、字体不同、大小不一、贴得歪。
 * 这是这五张唯一和另外八张不同的地方 —— **它们不是这台机器排的版**。
 *
 * ⚠️ 纸片必须**比字宽出一圈**，间距也要留够，否则相邻纸片会**盖掉字**。
 * 「缺笔画」和「贴得歪」是两回事：前者是 bug，后者才是素人。
 */
function ransom(
  card: CardDef,
  at: { x: number; y: number; size: number },
  seed: number,
  maxW = 86,
): Prim[] {
  // ⚠️ 参数用对象而不是位置 —— 第一版是 `ransom(card, x, y, size)`，
  //    调用时漏了 `size`，于是**把 seed 当字号传了**：字号十亿量级，
  //    五张里三张整张白掉（纸片大到糊满牌面），而报错是干净的。
  const { x, y, size } = at;
  const r = rng(seed);
  /**
   * 纸片之间的间隙。
   *
   * ⚠️ 这个数不是"看着差不多"定的：纸片各转 ±3.5°，一张高约 1.75em 的纸
   * 旋转后**角会横向吃掉约 0.6em**，两片相邻就是 1.2em。
   * 间隙小于它，纸片就会盖掉隔壁的**字** —— 而「缺笔画」和「贴得歪」
   * 是两回事：前者是 bug，后者才是素人。
   */
  const GAP = size * 0.26;
  // 字体角色也随机 —— 「每个字来自不同的地方」才是勒索信
  const FAM: readonly FontRole[] = ['mono', 'cjk', 'display', 'cjk', 'mono'];

  interface Scrap {
    ch: string;
    fs: number;
    w: number;
    h: number;
    rot: number;
    dy: number;
    fam: FontRole;
  }
  const scraps: Scrap[] = [];
  let total = 0;
  for (const ch of cardName(card).replace(/\s+/g, '')) {
    const fs = size * (0.82 + r() * 0.5);
    const pad = fs * 0.3;
    const w = fs * 1.12 + pad * 2;
    const h = fs * 1.15 + pad * 2;
    scraps.push({
      ch,
      fs,
      w,
      h,
      rot: (r() - 0.5) * 7,
      dy: (r() - 0.5) * size * 0.34,
      fam: FAM[Math.floor(r() * FAM.length)] ?? 'cjk',
    });
    total += w + GAP;
  }

  const k = total > maxW ? maxW / total : 1;
  const out: Prim[] = [];
  let ox = x;
  for (const s of scraps) {
    const w = s.w * k;
    const h = s.h * k;
    const fs = s.fs * k;
    const cx = ox + w / 2;
    const cy = y + s.dy * k;
    // 纸片是撕的，不是切的 —— 四角抖一下
    const paper = hand(rectPts(-w / 2, -h / 2, w, h), seed ^ (s.ch.codePointAt(0) ?? 0), 0.5, 2.4);
    out.push(
      group(
        [
          fillPoly(paper, solid(INK)),
          label(s.ch, 0, fs * 0.36, {
            size: fs,
            color: 0x000000,
            role: s.fam,
            weight: 900,
            anchor: 'middle',
          }),
        ],
        { at: pt(cx, cy), rotate: s.rot },
      ),
    );
    ox += w + GAP * k;
  }
  return out;
}

// ============================================================
// 逐张的画
// ============================================================

function insertArt(card: CardDef, seed: number): Prim[] {
  switch (card.local) {
    case 'Powerless1':
      return foodArt(seed);
    case 'Powerless2':
      return collarArt(seed);
    case 'Powerless3':
      return hoeArt(seed);
    case 'Powerless4':
      return seedArt(seed);
    case 'Powerless5':
      return snakeArt(seed);
    default:
      return [];
  }
}

/** ① 微妙的菜肴 —— 一坨**涂死**的东西 + 三条臭气线 */
function foodArt(seed: number): Prim[] {
  const out: Prim[] = [];
  // 盘子：手画的歪圈，而且**故意描了两道** —— 素人会把轮廓描两遍
  out.push(...inkPrims(handRing(50, 74, 33, 10.5, seed ^ 0x21, 52, 1.3, 1.5), seed ^ 0x21, solid(INK), {
    w: 1.5,
    varied: 0.28,
    freq: 3,
  }));
  // 臭气线
  for (let i = 0; i < 3; i++) {
    out.push(...inkPrims(stink(38 + i * 12, 47, 31, 3.4, 1.5, seed + i * 97), seed + i * 97, solid(INK), {
      w: 0.8,
      varied: 0.3,
      freq: 2.2,
    }));
  }
  // 那坨东西：横竖各涂一道。
  // ⚠️ 只涂一个方向会读成"夹层汉堡"（踩过）
  const scribble = crossScribble(50, 61, 20, 12, 8, seed ^ 0x11);
  out.push(...inkPrims(scribble.a, seed ^ 0x11, solid(INK), { w: 2.0, varied: 0.45, freq: 5.5 }));
  out.push(...inkPrims(scribble.b, seed ^ 0x12, solid(INK), { w: 1.3, varied: 0.45, freq: 6 }));
  // 第二道盘沿
  out.push(
    ...inkPrims(handRing(50, 74, 33, 10.5, seed ^ 0x22, 52, 2.0, 1.1), seed ^ 0x22, solid(INK), {
      w: 0.9,
      varied: 0.4,
      freq: 3.4,
    }),
  );
  return out;
}

/** ② 囚者颈环 —— 珠子环 + **挂在下面的一只死老鼠**（叉叉眼） */
function collarArt(seed: number): Prim[] {
  const out: Prim[] = [];
  const cx = 44;
  const cy = 37;

  // 珠子。⚠️ 小圆要 26 点以上、手抖压到 0.35 —— 16 点会出来六边形硬块（踩过）
  for (let i = 0; i < 7; i++) {
    const a = -2.4 + i * 0.8;
    const bx = cx + 21 * Math.cos(a);
    const by = cy + 19.5 * Math.sin(a);
    out.push(...halftoneDots(ring(bx, by, 3, 3, 26), 1.6, 45, 0.75).map((d) => dot(d.x, d.y, d.r, solid(INK))));
    out.push(
      ...inkPrims(handRing(bx, by, 3, 3, seed + i * 53, 26, 0.35, 2.8), seed + i * 53, solid(INK), {
        w: 0.9,
        varied: 0.22,
        freq: 2,
      }),
    );
  }
  out.push(...inkPrims(handRing(cx, cy, 21, 19.5, seed ^ 0x31, 56, 1.2, 1.4), seed ^ 0x31, solid(INK), {
    w: 1.8,
    varied: 0.28,
    freq: 2.6,
  }));

  // 老鼠的尾巴
  out.push(...inkPrims(hand([pt(44, 56), pt(43, 60), pt(45, 64)], seed ^ 0x77, 0.8, 2.6), seed ^ 0x77, solid(INK), {
    w: 1.0,
    varied: 0.25,
    freq: 1.6,
  }));
  // 耳朵：先画，身体后画 —— 身体盖住耳朵下半，才读得出"两只耳朵"
  for (const [ex, ey, s2] of [
    [38, 66, 0x99],
    [51, 66, 0xaa],
  ] as const) {
    out.push(
      ...halftoneDots(ring(ex, ey, 4.6, 4.6, 26), 1.7, 45, 0.8).map((d) => dot(d.x, d.y, d.r, solid(INK))),
      ...inkPrims(handRing(ex, ey, 4.6, 4.6, seed ^ s2, 26, 0.4, 2.8), seed ^ s2, solid(INK), {
        w: 1,
        varied: 0.22,
        freq: 2,
      }),
    );
  }
  // 身体
  const body = handRing(44, 76, 11.5, 6.8, seed ^ 0x88, 40, 1.0, 1.8);
  out.push(...halftoneDots(body, 1.9, 15, 0.72).map((d) => dot(d.x, d.y, d.r, solid(INK))));
  out.push(...inkPrims(body, seed ^ 0x88, solid(INK), { w: 1.2, varied: 0.26, freq: 2.4 }));
  // 叉叉眼：素人艺术表示"死了"的标准画法
  for (const [ex, ey] of [
    [39.5, 74.5],
    [48.5, 74.5],
  ] as const) {
    for (const seg of xEyes(ex, ey, 2.6, seed ^ 0xbb)) {
      out.push(...inkPrims(seg, seed ^ 0xbb, solid(INK), { w: 0.9, varied: 0.2, freq: 1 }));
    }
  }
  return out;
}

/** ③ 终极奉献 —— 一根歪柄 + **一颗巨大的宝石长在锄头尖上** + 一圈星芒 */
function hoeArt(seed: number): Prim[] {
  const out: Prim[] = [];
  // 柄
  out.push(...inkPrims(hand([pt(-14, 82), pt(10, 71), pt(34, 59), pt(56, 47)], seed ^ 0x41, 1.2, 1.7), seed ^ 0x41, solid(INK), {
    w: 2.4,
    varied: 0.34,
    freq: 2.8,
  }));
  // 两个节
  for (const [kx, ky, s2] of [
    [14, 69, 0x42],
    [36, 58, 0x43],
  ] as const) {
    out.push(
      ...inkPrims(handRing(kx, ky, 2.6, 2.6, seed ^ s2, 14, 0.6, 2.4), seed ^ s2, solid(INK), {
        w: 1.2,
        varied: 0.3,
        freq: 2.2,
      }),
    );
  }
  // 宝石。⚠️ 它是笑话本身：**投入和用途的落差**
  const gx = 70;
  const gy = 38;
  const GEM_SHAPE: readonly (readonly [number, number])[] = [
    [0, -1],
    [0.62, -0.52],
    [0.96, 0.1],
    [0.6, 0.62],
    [0, 0.84],
    [-0.6, 0.62],
    [-0.96, 0.1],
    [-0.62, -0.52],
    [0, -1],
  ];
  const gem = hand(
    GEM_SHAPE.map(([u, v]) => pt(gx + u * 16, gy + v * 16)),
    seed ^ 0x44,
    1.0,
    2.0,
  );
  out.push(...halftoneDots(gem, 2.2, 45, 0.62).map((d) => dot(d.x, d.y, d.r, solid(INK))));
  out.push(...inkPrims(gem, seed ^ 0x44, solid(INK), { w: 1.4, varied: 0.3, freq: 2.4 }));
  // 刻面
  out.push(
    ...inkPrims(hand([pt(gx - 15, gy - 1.5), pt(gx, gy + 3), pt(gx + 15, gy - 1.5)], seed ^ 0x45, 0.8, 2.2), seed ^ 0x45, solid(INK), {
      w: 0.8,
      varied: 0.2,
      freq: 1.4,
    }),
    ...inkPrims(hand([pt(gx, gy - 16), pt(gx, gy + 13)], seed ^ 0x46, 0.8, 2.2), seed ^ 0x46, solid(INK), {
      w: 0.8,
      varied: 0.2,
      freq: 1.4,
    }),
  );
  // 星芒。⚠️ 要画得特别真诚 —— 越真诚越好笑
  for (const [sx, sy, sr, k] of [
    [44, 18, 8, 0],
    [93, 23, 7, 1],
    [92, 60, 6, 2],
    [49, 62, 7, 3],
  ] as const) {
    for (const ray of sparkle(sx, sy, sr, seed ^ (0x50 + k * 31))) {
      out.push(...inkPrims(ray, seed ^ (0x50 + k * 31), solid(INK), { w: 0.5, varied: 0.55, freq: 1.2 }));
    }
  }
  for (const ray of sparkle(gx, gy - 25, 7, seed ^ 0x60)) {
    out.push(...inkPrims(ray, seed ^ 0x60, solid(INK), { w: 0.5, varied: 0.55, freq: 1.2 }));
  }
  return out;
}

/** ④ 甘草种子 —— **倒过来**的袋子 + 撒了一地 */
function seedArt(seed: number): Prim[] {
  const out: Prim[] = [];
  const sack = handRing(33, 58, 19, 21, seed ^ 0x71, 48, 1.5, 1.5);
  // 袋子整体倒过来，才读得出"撒了"
  const tilt = (pts: readonly Pt[]): Pt[] => rotateAbout(pts, -19, 33, 58);
  out.push(...halftoneDots(sack, 2.0, 45, 0.6).map((d) => dot(d.x, d.y, d.r, solid(INK))));
  out.push(...inkPrims(tilt(sack), seed ^ 0x71, solid(INK), { w: 1.4, varied: 0.3, freq: 2.6 }));
  const neck = hand([pt(22, 38), pt(44, 38), pt(46, 45), pt(20, 45)], seed ^ 0x72, 1.0, 2.2);
  out.push(...inkPrims(tilt(neck), seed ^ 0x72, solid(INK), { w: 1.5, varied: 0.3, freq: 2.2 }));
  const frill = hand(
    [pt(22, 38), pt(20, 32), pt(26, 35), pt(30, 30), pt(35, 35), pt(40, 31), pt(44, 37)],
    seed ^ 0x73,
    1.0,
    1.8,
  );
  out.push(...inkPrims(tilt(frill), seed ^ 0x73, solid(INK), { w: 1.2, varied: 0.35, freq: 2.4 }));

  // 种子。⚠️ 必须**二维撒开** —— 沿一条线摆就成了链子（踩过）
  const r = rng(seed ^ 0x74);
  for (let i = 0; i < 15; i++) {
    const sx = 52 + r() * 41;
    const sy = 36 + r() * 42;
    const sr = 2.4 + r() * 2.0;
    out.push(
      ...inkPrims(handRing(sx, sy, sr, sr * 0.74, seed + i * 37, 12, 0.6, 2.6), seed + i * 37, solid(INK), {
        w: 1.2,
        varied: 0.4,
        freq: 2,
      }),
    );
  }
  return out;
}

/**
 * ⑤ 蛇（不）咬 —— **嘴被胶带封住的蛇**。
 *
 * 原图是一条龇牙的凶蛇，牌名却叫"蛇不咬"、效果是"保留。" —— 笑点是
 * **画面和效果的落差**。所以照样画得很凶，只是嘴被封住了。
 *
 * ⚠️ 关键是**简单**。涂鸦好笑是因为三笔就画完了；把头生成成一团被噪声
 * 啃烂的土豆，笑点就没了。所以噪声压到 1.1。
 */
function snakeArt(seed: number): Prim[] {
  const out: Prim[] = [];
  const N = 44;
  const raw = (t: number): Pt => pt(-34 + 100 * t, 74 - 32 * t + 7 * Math.sin(t * Math.PI * 1.4));
  const spine = hand(
    Array.from({ length: N + 1 }, (_, i) => raw(i / N)),
    seed ^ 0x81,
    1.1,
    1.6,
  );
  // 身子：尾巴细、脖子粗
  out.push(
    ...inkPrims(spine, seed ^ 0x81, solid(INK), { w: (t) => 1.2 + 2.0 * t, varied: 0.34, freq: 1.8 }),
  );

  // 头：沿切向拉长的歪圆。
  // ⚠️ 必须拉长 —— 头短了胶带会盖住吻部，可见部分只剩一个圆球，
  //    读成"一根棍子穿了个球"（踩过）
  const tip = spine[N] ?? pt(66, 42);
  const prv = spine[N - 4] ?? tip;
  let hx = tip.x - prv.x;
  let hy = tip.y - prv.y;
  const hl = Math.hypot(hx, hy) || 1;
  hx /= hl;
  hy /= hl;
  const hcx = tip.x + hx * 5.5;
  const hcy = tip.y + hy * 5.5;
  const L2 = (lx: number, ly: number): Pt => pt(hcx + hx * lx - hy * ly, hcy + hy * lx + hx * ly);
  const head = hand(
    ring(0, 0, 13.5, 9.4, 40).map((p) => L2(p.x * 1.7 + 4, p.y)),
    seed ^ 0x82,
    1.1,
    2.0,
  );
  out.push(fillPoly(head, solid(0x000000)));
  out.push(fillPoly(head, solid(INK, 0.1)));
  out.push(...inkPrims(head, seed ^ 0x82, solid(INK), { w: 1.5, varied: 0.28, freq: 2.6 }));

  // 胶带：横缠在吻部中段，两头都翘出头外一截 —— 笑话就在这两截上
  const TU = (v: number): number => 6.0 + 0.13 * v;
  const TD = (v: number): number => 11.0 + 0.13 * v;
  const TV = 13;
  out.push(
    fillPoly(
      [L2(TU(-TV), -TV), L2(TU(TV), TV), L2(TD(TV), TV), L2(TD(-TV), -TV)],
      solid(INK),
      { width: 0.7, color: 0x000000 },
    ),
  );

  // 眼睛：一大一小，瞳孔各看各的 —— 这张牌的笑点不在蛇，在它的表情
  const eye = (lx: number, ly: number, rr: number, px: number, py: number): void => {
    const c = L2(lx, ly);
    const p = L2(lx + px, ly + py);
    out.push(dot(c.x, c.y, rr, solid(INK)));
    out.push(dot(p.x, p.y, rr * 0.44, solid(0x000000)));
  };
  eye(2.0, -4.6, 3.3, 0.9, -1.0);
  eye(-3.0, -5.4, 2.5, -1.0, 0.6);
  return out;
}
